import { Component } from "../base/Component";
import { ComponentFactory } from "../../service/ComponentFactory";
import { PageContext } from "../../service/PageContext";
import { SavedPageService } from "../../service/SavedPageService";
import {
  fetchSavedForSession,
  HealioSavedRow,
  parseSavedMeta,
} from "../../service/HealioWidgetsSavedApi";

/** Saved targetTypes for each list: live saves use the raw type, migrated rows the newer name. */
const SERIES_TYPES = ["podcastseries", "podcast-series"];
const EPISODE_TYPES = ["podcastepisode", "podcast-episode"];

/**
 * Renders the podcast landing Saved tab. The user's saved series and episodes
 * come from healionetwork-be (/healiowidgets/api/saved/me); their ids go to the
 * SavedTab endpoint, which renders the cards from Sitecore. Refreshed when the
 * tab is shown and after a save / unsave.
 */
export class PodcastSavedTabLoader extends Component {
  /**
   * Set when the user saves/unsaves while another tab is visible; survives a full
   * navigation to `?tab=saved` (same tab) so we keep refreshing longer than usual.
   */
  private static readonly pendingSavedRefreshKey = "podcast2026.pendingSavedTabRefresh";

  private readonly refreshUrl: string;
  /** e.g. "healiotwo.web": saves made on another site (a .master preview) are not listed. */
  private readonly siteKey: string;
  /** Monotonically increases so stale fetch responses are ignored (last write wins). */
  private refreshGeneration = 0;
  private readonly tabLinkSelector = "#podcast-tab-saved";
  private readonly savedPaneId = "podcast-pane-saved";
  private saveMutationTimer: number | null = null;
  private tabShownRetryTimers: number[] = [];

  constructor(element: HTMLElement, pageContext: PageContext) {
    super(element, pageContext);
    this.refreshUrl = element.dataset.savedTabRefreshUrl || "";
    this.siteKey = (element.dataset.savedSite || "").toLowerCase();
  }

  public async init(): Promise<void> {
    if (!this.refreshUrl) {
      return;
    }
    this.wireTabShown();
    this.wireSaveMutations();
    this.wirePageShow();
    this.refreshIfSavedPaneIsActive();
  }

  private wireTabShown(): void {
    const w = window as Window & { $j?: any; jQuery?: any };
    const jq = w.$j || w.jQuery;
    const run = () => this.scheduleTabShownRefreshes(false);

    // Bootstrap 4 tab events are dispatched through jQuery; delegated handler is reliable.
    if (jq) {
      jq(document).on("shown.bs.tab", this.tabLinkSelector, run);
    } else {
      const tabLink = document.querySelector(this.tabLinkSelector);
      tabLink?.addEventListener("shown.bs.tab", run);
    }
  }

  /**
   * Immediate fetch plus delayed retries — saves are sent without waiting, so the list can briefly lag a save.
   * Logged-in users navigate to Saved via full page load (`?tab=saved`), so we rely on
   * this schedule (not `shown.bs.tab`) plus optional extended polling when a save
   * happened while Discover was visible (see `setPendingSavedTabRefresh`).
   */
  private scheduleTabShownRefreshes(extended: boolean, initialLoad = false): void {
    for (const t of this.tabShownRetryTimers) {
      window.clearTimeout(t);
    }
    this.tabShownRetryTimers = [];

    // A plain page load has no save in flight to wait for: one request, shared with the save icons.
    if (initialLoad && !extended) {
      void this.refreshSavedTab(false);
      return;
    }

    void this.refreshSavedTab();
    const delays = extended
      ? [450, 1200, 2500, 5000, 8000, 12000]
      : [450, 1200];
    for (const ms of delays) {
      this.tabShownRetryTimers.push(
        window.setTimeout(() => void this.refreshSavedTab(), ms)
      );
    }
  }

  private wireSaveMutations(): void {
    const onMut = () => this.scheduleSaveMutationRefresh();
    window.addEventListener(SavedPageService.SavedPageIncrementEvent, onMut);
    window.addEventListener(SavedPageService.SavedPageDecrementEvent, onMut);
    window.addEventListener("SaveIconSaved", onMut);
    window.addEventListener("SaveIconRemoved", onMut);
  }

  private scheduleSaveMutationRefresh(): void {
    const pane = document.getElementById(this.savedPaneId);
    if (!pane || !pane.classList.contains("active")) {
      this.setPendingSavedTabRefresh();
      return;
    }
    if (this.saveMutationTimer !== null) {
      window.clearTimeout(this.saveMutationTimer);
    }
    this.saveMutationTimer = window.setTimeout(() => {
      this.saveMutationTimer = null;
      void this.refreshSavedTab();
    }, 400);
  }

  private wirePageShow(): void {
    window.addEventListener("pageshow", ev => {
      const pageEv = ev as PageTransitionEvent;
      if (!pageEv.persisted) {
        return;
      }
      const pane = document.getElementById(this.savedPaneId);
      if (pane && pane.classList.contains("active")) {
        this.scheduleTabShownRefreshes(this.takePendingSavedTabRefresh());
      }
    });
  }

  /**
   * On first load, Saved can already be active (e.g. ?tab=saved).
   * Refresh immediately so we do not rely on a tab-shown event firing.
   */
  private refreshIfSavedPaneIsActive(): void {
    const pane = document.getElementById(this.savedPaneId);
    if (!pane || !pane.classList.contains("active")) {
      return;
    }

    this.scheduleTabShownRefreshes(this.takePendingSavedTabRefresh(), true);
  }

  private setPendingSavedTabRefresh(): void {
    try {
      sessionStorage.setItem(PodcastSavedTabLoader.pendingSavedRefreshKey, "1");
    } catch {
      // private mode / disabled storage
    }
  }

  private takePendingSavedTabRefresh(): boolean {
    try {
      if (sessionStorage.getItem(PodcastSavedTabLoader.pendingSavedRefreshKey) === "1") {
        sessionStorage.removeItem(PodcastSavedTabLoader.pendingSavedRefreshKey);
        return true;
      }
    } catch {
      // ignore
    }
    return false;
  }

  private idsOfTypes(rows: HealioSavedRow[], types: string[]): string[] {
    return rows
      .filter(row => types.indexOf((row.targetType || "").toLowerCase()) >= 0)
      .filter(row => {
        const site = (parseSavedMeta(row.data ?? undefined).site || "").toLowerCase();
        return !site || !this.siteKey || site === this.siteKey;
      })
      .map(row => row.targetId);
  }

  private async savedTabUrl(fresh: boolean): Promise<string> {
    // First load shares the fetch the save icons already made; retries and
    // post-save refreshes must see the latest rows.
    const service = this._pageContext ? this._pageContext.savedPageService : null;
    if (fresh) {
      service?.clearCache();
    }
    const rows = service ? await service.listAllRows() : await fetchSavedForSession();
    const params = new URLSearchParams({
      seriesIds: this.idsOfTypes(rows, SERIES_TYPES).join(","),
      episodeIds: this.idsOfTypes(rows, EPISODE_TYPES).join(","),
      _: String(Date.now()),
    });
    const base = this.refreshUrl;
    const sep = base.indexOf("?") >= 0 ? "&" : "?";

    return `${base}${sep}${params.toString()}`;
  }

  private async refreshSavedTab(fresh = true): Promise<void> {
    if (!this.refreshUrl) {
      return;
    }
    const id = ++this.refreshGeneration;
    try {
      const url = await this.savedTabUrl(fresh);
      if (id !== this.refreshGeneration) {
        return;
      }
      const res = await fetch(url, {
        cache: "no-store",
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest" }
      });
      if (!res.ok) {
        return;
      }
      const html = await res.text();
      if (id !== this.refreshGeneration) {
        return;
      }
      this._element.innerHTML = html;
      await this._pageContext?.componentFactory.loadComponents(this._element);
    } catch (e) {
      console.error("PodcastSavedTabLoader: refresh failed", e);
    }
  }
}

ComponentFactory.registerComponent("PodcastSavedTabLoader", PodcastSavedTabLoader);
